// src/pages/Admin/UserAdmin/SpAccountPurgePage.tsx
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Badge, Button, Container, Form,
  Modal, Pagination, Spinner, Table,
} from 'react-bootstrap';
import { FaBroom, FaUserMinus } from 'react-icons/fa';
import {
  createPurgeRequest, getPurgeReportRuns,
  PagedResult, PurgeReportRun,
} from '@/api/userAdminApi';
import { searchUsers } from '@/services/resourcesApi';
import './SpAccountPurgePage.scss';

const PAGE_SIZE = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : '—');

function runDuration(start?: string, end?: string): string {
  if (!start || !end) return '—';
  const secs = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function statusVariant(s: string) {
  if (s === 'COMPLETE') return 'success';
  if (s === 'ERROR') return 'danger';
  if (s === 'RUNNING') return 'primary';
  return 'secondary';
}

function PaginationBar({
  page, pageSize, total, onChange,
}: {
  page: number; pageSize: number; total: number; onChange: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <small className="text-muted">
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
      </small>
      <Pagination size="sm" className="mb-0">
        <Pagination.Prev disabled={page === 1} onClick={() => onChange(page - 1)} />
        {page > 2 && <Pagination.Item onClick={() => onChange(1)}>1</Pagination.Item>}
        {page > 3 && <Pagination.Ellipsis />}
        {page > 1 && <Pagination.Item onClick={() => onChange(page - 1)}>{page - 1}</Pagination.Item>}
        <Pagination.Item active>{page}</Pagination.Item>
        {page < totalPages && <Pagination.Item onClick={() => onChange(page + 1)}>{page + 1}</Pagination.Item>}
        {page < totalPages - 2 && <Pagination.Ellipsis />}
        {page < totalPages - 1 && <Pagination.Item onClick={() => onChange(totalPages)}>{totalPages}</Pagination.Item>}
        <Pagination.Next disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </Pagination>
    </div>
  );
}

// ─── Create Purge Modal ────────────────────────────────────────────────────────

interface CreatePurgeModalProps {
  show: boolean;
  onHide: () => void;
  onCreated: () => void;
}

function CreatePurgeModal({ show, onHide, onCreated }: CreatePurgeModalProps) {
  const [email, setEmail] = useState('');
  const [includeOneDrive, setIncludeOneDrive] = useState(false);
  const [removeAccess, setRemoveAccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Typeahead state
  const [suggestions, setSuggestions] = useState<{ displayName: string; email: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset on open
  useEffect(() => {
    if (show) {
      setEmail('');
      setIncludeOneDrive(false);
      setRemoveAccess(false);
      setBusy(false);
      setError(null);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [show]);

  // Debounced user search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const term = email.trim();
    if (term.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    let cancelled = false;
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchUsers(term);
        if (cancelled) return;
        const mapped = (results as any[])
          .map((u) => {
            const userEmail = (u.mail || u.userPrincipalName || u.email || u.upn || '').trim();
            if (!userEmail) return null;
            return { displayName: (u.displayName || userEmail).trim(), email: userEmail };
          })
          .filter((x): x is { displayName: string; email: string } => Boolean(x))
          .slice(0, 8);
        setSuggestions(mapped);
        setShowSuggestions(mapped.length > 0);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);

    return () => { cancelled = true; };
  }, [email]);

  function selectSuggestion(s: { displayName: string; email: string }) {
    setEmail(s.email);
    setSuggestions([]);
    setShowSuggestions(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) { setError('Email is required'); return; }
    setBusy(true);
    setError(null);
    try {
      await createPurgeRequest({ email: email.trim(), includeOneDrive, removeAccess });
      onCreated();
      onHide();
    } catch {
      setError('Failed to submit purge request. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal show={show} onHide={onHide} backdrop="static" centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUserMinus className="me-2 text-danger" />
            Remove Account From SharePoint
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

          <Form.Group className="mb-3" controlId="purgeEmail">
            <Form.Label className="fw-semibold">User Email Address</Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder="Search by name or enter email address…"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                autoComplete="off"
                disabled={busy}
              />
              {searching && (
                <div className="spa-search-spinner">
                  <Spinner size="sm" animation="border" variant="secondary" />
                </div>
              )}
              {showSuggestions && suggestions.length > 0 && (
                <ul className="spa-suggestions">
                  {suggestions.map((s) => (
                    <li key={s.email} onMouseDown={() => selectSuggestion(s)}>
                      <span className="fw-semibold">{s.displayName}</span>
                      <span className="text-muted small ms-2">{s.email}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Form.Text className="text-muted">
              Note: SharePoint maintains a separate directory. If the user is not found in search, enter the email address directly.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold d-block">Include OneDrive</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="onedrive-yes"
                label="Yes"
                checked={includeOneDrive}
                onChange={() => setIncludeOneDrive(true)}
                disabled={busy}
              />
              <Form.Check
                type="radio"
                id="onedrive-no"
                label="No"
                checked={!includeOneDrive}
                onChange={() => setIncludeOneDrive(false)}
                disabled={busy}
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label className="fw-semibold d-block">Remove Access</Form.Label>
            <div className="d-flex gap-3">
              <Form.Check
                type="radio"
                id="remove-yes"
                label="Yes — live execution"
                checked={removeAccess}
                onChange={() => setRemoveAccess(true)}
                disabled={busy}
              />
              <Form.Check
                type="radio"
                id="remove-no"
                label="No — dry run only"
                checked={!removeAccess}
                onChange={() => setRemoveAccess(false)}
                disabled={busy}
              />
            </div>
            {removeAccess && (
              <Alert variant="warning" className="mt-2 mb-0 py-2 small">
                <strong>Live execution</strong> — the account will be removed from each site&apos;s user list. Access granted through groups (e.g. M365 group membership) is not affected.
              </Alert>
            )}
            {!removeAccess && (
              <Form.Text className="text-muted">
                Dry run: the report will show what would be removed without making changes.
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={busy}>Cancel</Button>
          <Button variant="danger" type="submit" disabled={busy || !email.trim()}>
            {busy ? <><Spinner size="sm" animation="border" className="me-1" />Submitting…</> : 'Remove Account From SharePoint'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const SpAccountPurgePage: React.FC = () => {
  const [data, setData] = useState<PagedResult<PurgeReportRun> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getPurgeReportRuns(page, PAGE_SIZE));
    } catch {
      setError('Failed to load purge report runs');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  function handleCreated() {
    setPage(1);
    load();
  }

  return (
    <Container className="mt-4 spa-page">
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-1">
            <FaBroom className="me-2 text-danger" />
            SharePoint Account Purge
          </h2>
          <p className="text-muted mb-0">
            Remove a user from SharePoint site user lists (optionally including OneDrive sites). Use to resolve account collisions.
          </p>
        </div>
        <Button variant="danger" onClick={() => setShowModal(true)}>
          <FaUserMinus className="me-2" />
          Remove Account From SharePoint
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="spa-table">
              <thead>
                <tr>
                  <th style={{ width: 210 }}>User Email</th>
                  <th style={{ width: 120 }}>OneDrive</th>
                  <th style={{ width: 120 }}>Mode</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ width: 160 }}>Requested By</th>
                  <th style={{ width: 170 }}>Submitted</th>
                  <th style={{ width: 90 }}>Duration</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((r) => {
                  const isOpen = expanded === r.m365ReportRunId;
                  return (
                    <React.Fragment key={r.m365ReportRunId}>
                      <tr
                        className={isOpen ? 'table-active' : ''}
                        style={{ cursor: r.reportResult ? 'pointer' : undefined }}
                        onClick={() => r.reportResult && setExpanded(isOpen ? null : r.m365ReportRunId)}
                      >
                        <td className="text-nowrap">{r.email || '—'}</td>
                        <td>
                          <Badge bg={r.includeOneDrive ? 'info' : 'secondary'}>
                            {r.includeOneDrive ? 'Yes' : 'No'}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={r.isTest ? 'secondary' : 'danger'}>
                            {r.isTest ? 'Dry Run' : 'Live'}
                          </Badge>
                        </td>
                        <td>
                          <Badge bg={statusVariant(r.reportStatus)}>{r.reportStatus}</Badge>
                        </td>
                        <td className="text-nowrap text-truncate" style={{ maxWidth: 160 }} title={r.createUser}>
                          {r.createUser || '—'}
                        </td>
                        <td className="text-nowrap">{fmt(r.createDt)}</td>
                        <td className="text-muted">{runDuration(r.reportStartDate, r.reportEndDate)}</td>
                        <td>
                          {r.reportResult ? (
                            <span className="text-primary small" style={{ cursor: 'pointer' }}>
                              {isOpen ? 'Hide' : 'View'}
                            </span>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                      </tr>
                      {isOpen && r.reportResult && (
                        <tr key={`${r.m365ReportRunId}-detail`}>
                          <td colSpan={8}>
                            <pre className="spa-result-raw">{r.reportResult}</pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {data?.items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      No purge runs found. Use the button above to submit a new request.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          {data && (
            <PaginationBar
              page={page}
              pageSize={PAGE_SIZE}
              total={data.totalCount}
              onChange={setPage}
            />
          )}
        </>
      )}

      <CreatePurgeModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onCreated={handleCreated}
      />
    </Container>
  );
};

export default SpAccountPurgePage;
